import {
	ContractSuretyAccountUnderwritingModelSize,
	type Currency,
	Dtos,
	getBaseNaicsCode,
	type NAICSCode,
} from "@inrev/common";
import { Option } from "@inrev/core";

export type AdminAccountCompany = Dtos.Admin.SuretyAccount.Get.Response.Company;
export type AdminAccountIndividual = Dtos.Admin.SuretyAccount.Get.Response.Individual;

export const computeAdjustedFsr = (companies: AdminAccountCompany[]): number | undefined => {
	const fsrArr = companies
		.filter((company) => company.config.contract.includeInUnderwriting)
		.flatMap((company) =>
			company.creditReports.flatMap((report) => {
				const value = report.creditProfile?.experianFsr;
				return Option.mapOrElse(value, (fsr) => [fsr], []);
			}),
		);
	if (fsrArr.length === 0) return undefined;
	return Math.max(...fsrArr);
};

export const computeAverageFico = (individuals: AdminAccountIndividual[]): number | undefined => {
	const ficoArr = individuals.flatMap((individual) =>
		individual.creditReports.flatMap((report) =>
			report.outcome.status === "success"
				? Option.mapOrElse(report.outcome.score, (score) => [score], [])
				: [],
		),
	);
	if (ficoArr.length === 0) return undefined;
	return Math.round(ficoArr.reduce((acc, fico) => acc + fico, 0) / ficoArr.length);
};

const MODEL_SIZE_TO_SEGMENT: Record<ContractSuretyAccountUnderwritingModelSize, 1 | 2 | 3> = {
	[ContractSuretyAccountUnderwritingModelSize.small]: 1,
	[ContractSuretyAccountUnderwritingModelSize.medium]: 2,
	[ContractSuretyAccountUnderwritingModelSize.large]: 3,
};

const REVENUE_THRESHOLDS = {
	ONE_M: 1_000_000,
	TEN_M: 10_000_000,
	FIFTY_M: 50_000_000,
	TWO_FIFTY_M: 250_000_000,
	ONE_B: 1_000_000_000,
} as const;

const getOwnershipSegment = (account: {
	companies: readonly unknown[];
	individuals: readonly unknown[];
}): 0 | 1 | 2 => {
	if (account.individuals.length === 0) return 2; // PE — entity-owned, no individual owners
	if (account.companies.length === 1) return 1; // Closely Held — single primary company + individuals
	return 0; // Unknown — Public is never detectable today
};

const getNaicsSegment = (naicsCodes: readonly string[] | null | undefined): string => {
	if (!naicsCodes || naicsCodes.length === 0) return "0";
	const base = getBaseNaicsCode(naicsCodes[0]);
	const hasMultipleBases = naicsCodes.some((code) => getBaseNaicsCode(code) !== base);
	return hasMultipleBases ? `${base}M` : base;
};

const getRevenueBucket = (revenue: Currency | undefined | null): string => {
	if (revenue == null) return "0";
	if (revenue < REVENUE_THRESHOLDS.ONE_M) return "1";
	if (revenue < REVENUE_THRESHOLDS.TEN_M) return "2"; // $1M exactly → 2
	if (revenue < REVENUE_THRESHOLDS.FIFTY_M) return "3";
	if (revenue < REVENUE_THRESHOLDS.TWO_FIFTY_M) return "4";
	if (revenue < REVENUE_THRESHOLDS.ONE_B) return "5";
	return "6";
};

const getModelSizeSegment = (modelSize: ContractSuretyAccountUnderwritingModelSize): string =>
	String(MODEL_SIZE_TO_SEGMENT[modelSize]);

type UwModelAccount = {
	companies: readonly unknown[];
	individuals: readonly unknown[];
	isContractSurety?: boolean;
};

export const getUwModelDisplayName = (
	account: UwModelAccount,
	fyeRevenue: Currency | undefined | null,
	modelSize: ContractSuretyAccountUnderwritingModelSize,
	naicsCodes?: readonly NAICSCode[],
): string => {
	const suretyType = account.isContractSurety === false ? "0" : "CON";
	const ownership = getOwnershipSegment(account);
	const naics = getNaicsSegment(naicsCodes);
	const revenue = getRevenueBucket(fyeRevenue);
	return `${suretyType}-${ownership}-${naics}-${revenue}-${getModelSizeSegment(modelSize)}`;
};
